import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { fetchYoutubePreview, YoutubeOEmbedResponse } from "@/services/api/youtube";
import ChatItemHrefButtons from "@/components/waves/ChatItemHrefButtons";
import { getYoutubeFetchUrl, parseYoutubeLink } from "./youtube";
const normalizeYoutubeHtml = (html: string): string => {
  let normalized = html.replace(/width="[^"]*"/i, 'width="100%"');
  normalized = normalized.replace(/height="[^"]*"/i, 'height="100%"');
  if (/style="[^"]*"/i.test(normalized)) {
    normalized = normalized.replace(/style="([^"]*)"/i, (_, styles: string) => {
      const cleanedStyles = styles.replace(/;?\s*$/, "");
      return `style="${cleanedStyles};width:100%;height:100%;"`;
    });
  } else {
    normalized = normalized.replace(/<iframe/i, '<iframe style="width:100%;height:100%;"');
  }
  return normalized;
};
interface YoutubePreviewProps {
  readonly href: string;
}
const YoutubePreview = t0 => {
  const $ = _c(31);
  const {
    href
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = parseYoutubeLink(href);
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkInfo = t1;
  if (!linkInfo) {
    throw new Error("Invalid YouTube link");
  }
  const {
    videoId
  } = linkInfo;
  const [preview, setPreview] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  let t2;
  let t3;
  if ($[2] !== href || $[3] !== videoId) {
    t2 = () => {
      const abortController = new AbortController();
      let isActive = true;
      setPreview(null);
      setHasError(false);
      setShowEmbed(false);
      const fetchUrl = getYoutubeFetchUrl(href, videoId);
      fetchYoutubePreview(fetchUrl, abortController.signal).then(data => {
        if (!isActive) {
          return;
        }
        if (data) {
          setPreview({
            ...data,
            html: normalizeYoutubeHtml(data.html)
          });
        } else {
          setHasError(true);
        }
      }).catch(error => {
        if (!isActive) {
          return;
        }
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setHasError(true);
      });
      return () => {
        isActive = false;
        abortController.abort();
      };
    };
    t3 = [href, videoId];
    $[2] = href;
    $[3] = videoId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (hasError) {
    throw new Error("Failed to load YouTube preview");
  }
  if (!preview) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-flex-1 tw-min-w-0"><div className="tw-aspect-video tw-w-full tw-rounded-lg tw-bg-iron-800 tw-animate-pulse" /></div>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== href) {
      t5 = <div className="tw-flex tw-items-stretch tw-w-full tw-gap-x-1">{t4}<ChatItemHrefButtons href={href} /></div>;
      $[7] = href;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  const ariaLabel = preview.title ? `Play YouTube video ${preview.title}` : `Play YouTube video ${videoId}`;
  let t4;
  if ($[9] !== ariaLabel || $[10] !== preview.html || $[11] !== preview.thumbnail_url || $[12] !== preview.title || $[13] !== showEmbed || $[14] !== videoId) {
    t4 = <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-black">{showEmbed ? <div className="tw-relative tw-w-full tw-aspect-video tw-bg-black" data-testid="youtube-embed" dangerouslySetInnerHTML={{
        __html: preview.html
      }} /> : <button type="button" className="tw-relative tw-w-full tw-aspect-video tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer" onClick={() => setShowEmbed(true)} aria-label={ariaLabel}><img src={preview.thumbnail_url} alt={preview.title ?? `YouTube video ${videoId}`} className="tw-h-full tw-w-full tw-object-cover" /><div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/40"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="tw-h-12 tw-w-12 tw-text-white tw-opacity-90" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg></div></button>}</div>;
    $[9] = ariaLabel;
    $[10] = preview.html;
    $[11] = preview.thumbnail_url;
    $[12] = preview.title;
    $[13] = showEmbed;
    $[14] = videoId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== preview.title) {
    t5 = preview.title && <p className="tw-text-sm tw-font-semibold tw-text-iron-100 tw-mb-0">{preview.title}</p>;
    $[16] = preview.title;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== preview.author_name) {
    t6 = preview.author_name && <p className="tw-text-xs tw-text-iron-400 tw-mb-0">{preview.author_name}</p>;
    $[18] = preview.author_name;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <div className="tw-mt-2 tw-space-y-1">{t5}{t6}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t4 || $[24] !== t7) {
    t8 = <div className="tw-flex-1 tw-min-w-0">{t4}{t7}</div>;
    $[23] = t4;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== href) {
    t9 = <ChatItemHrefButtons href={href} />;
    $[26] = href;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="tw-flex tw-items-stretch tw-w-full tw-gap-x-1">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
export default YoutubePreview;